import { ColumnRegular } from '@revolist/react-datagrid';

import {
  SelectOption,
  createSelectCellTemplate,
  createSelectEditor,
} from 'src/components/streametric/revoGridTable';
import { GatewayOptionsEnum, GatewayOptionsTypes } from 'src/types/gateways';

import featureFlags from '../../../feature-flags.json';
import dataTypes from '../data/data-types.json';

/**
 * A single column of the v2 tag table. It is RevoGrid's own column type — the
 * table head, the cell renderer and the editor are one declaration there, not
 * three — so `name` is the header label and `prop` is the key it reads off the
 * row model (`GatewayInput`, flattened where the value lives under
 * `advancedTagMapping`).
 */
export type GatewayTagColumn = ColumnRegular;

const ADVANCED_TAG_MAPPING_ENABLED = featureFlags.advancedTagMapping === 1;

/**
 * Tag Type options, from the same `data/data-types.json` v1's RHFAutocomplete
 * reads. The model stores the value (`alarm`), the user sees the label
 * (`Alarm`) — both the editor and the resting-state replica map between them.
 */
export const TAG_TYPE_OPTIONS: SelectOption[] = dataTypes;
/** Built once at module scope: `columns` has to stay a stable reference. */
const tagTypeEditor = createSelectEditor(TAG_TYPE_OPTIONS);

const ADVANCED_TAG_MAPPING_COLUMNS: GatewayTagColumn[] = [
  { prop: 'controller', name: 'Controller', size: 180, readonly: true },
  { prop: 'acceptableRangeValue', name: 'Acceptable Range/Value', size: 180, readonly: true },
  { prop: 'dataTypeOfValues', name: 'Data Type', size: 140, readonly: true },
  { prop: 'controllable', name: 'Controllable', size: 120, readonly: true },
];

/**
 * The raw-input head — v1's `RAW_INPUT_GATEWAY_TABLE_HEAD`, which it uses for
 * every gateway that is not EWON or Kallipr. Those two are the exception (no
 * advanced-tag-mapping columns, `EWON_GATEWAY_TABLE_HEAD`), so the types below
 * share this one list rather than each declaring its own copy.
 *
 * The advanced-tag-mapping block is spread in on the same feature flag v1 uses,
 * and `EditGatewayTableRow` renders those cells under the same not-EWON,
 * not-Kallipr condition.
 */
export const RAW_INPUT_TAG_TABLE_COLUMNS: GatewayTagColumn[] = [
  { prop: 'rawInputName', name: 'Raw Input Names', size: 200, readonly: true },
  {
    /**
     * The dropdown column — a real MUI <Select> on edit, and an outlined-input
     * replica at rest so the column looks like the v1 field even when no cell
     * is being edited. `editor` takes an EditorCtr directly, so the grid needs
     * no `editors` map.
     *
     * Sorting and filtering work on the MODEL value, so they are unaffected by
     * the replica; a bulk write (fill handle, paste) bypasses the editor and
     * its option list entirely, which is worth policing once those are on.
     */
    prop: 'dataType',
    name: 'Tag Type',
    // wider than the text columns: the outlined field eats the 32px cell
    // gutter, a 14px left pad and a 24px chevron before any text shows
    size: 220,
    editor: tagTypeEditor,
    cellTemplate: createSelectCellTemplate(TAG_TYPE_OPTIONS),
  },
  { prop: 'rawUnit', name: 'Raw Unit', size: 180 },
  { prop: 'displayName', name: 'Tag Display Name', size: 200 },
  ...(ADVANCED_TAG_MAPPING_ENABLED ? ADVANCED_TAG_MAPPING_COLUMNS : []),
  { prop: 'hidden', name: 'Hide', size: 100 },
];

export const GATEWAY_TAG_TABLE_COLUMNS: Partial<Record<GatewayOptionsTypes, GatewayTagColumn[]>> = {
  [GatewayOptionsEnum.StreamLite]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.StreamMLitePayload]: RAW_INPUT_TAG_TABLE_COLUMNS,
  [GatewayOptionsEnum.Inhand]: RAW_INPUT_TAG_TABLE_COLUMNS,
  /**
   * Still to come. FX30 takes `RAW_INPUT_TAG_TABLE_COLUMNS` as well; EWON and
   * Kallipr need a list WITHOUT the advanced-tag-mapping columns.
   */
};

/**
 * Columns for `gatewayType`, or an empty list for a type that has no v2 table
 * yet — deliberately not a guessed default, since the wrong head would render
 * columns the gateway has no data for.
 */
export const getGatewayTagTableColumns = (gatewayType?: GatewayOptionsTypes): GatewayTagColumn[] =>
  (gatewayType && GATEWAY_TAG_TABLE_COLUMNS[gatewayType]) || [];
